import { useRef, useState } from "react";
import { Phone, Mail, ImagePlus, X, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { api, apiError } from "@/lib/api";
import { NEED_TYPES } from "@/lib/constants";

export default function QuoteSection({ settings }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", client_type: "particulier", need_type: NEED_TYPES[0], message: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list)].slice(0, 6);
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("photos", f));
      await api.post("/quotes", fd);
      setSent(true);
      toast.success("Votre demande a bien été envoyée. Nous revenons vers vous rapidement.");
    } catch (err) {
      toast.error(apiError(err, "Impossible d'envoyer la demande. Réessayez."));
    } finally {
      setSending(false);
    }
  };

  const tel = (settings.phone || "").replace(/\s/g, "");
  const inputCls =
    "w-full rounded-xl border border-line bg-cream px-4 py-3 text-base text-ink placeholder:text-muted2 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest transition";

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream text-ink pt-16 sm:pt-24 pb-32 md:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2 lg:gap-20 items-start">
        <Reveal>
          <Eyebrow>Un besoin ?</Eyebrow>
          <h2 className="mt-3 font-display font-semibold tracking-tight leading-snug text-3xl sm:text-4xl lg:text-5xl text-ink" data-testid="contact-title">
            Parlons-en.
          </h2>
          <p className="mt-5 text-body2 text-base sm:text-lg leading-relaxed max-w-md">
            Entretien ponctuel, remise en état ou intervention régulière : décrivez-nous simplement votre besoin.
          </p>
          <p className="mt-4 text-sm text-muted2 font-medium">Réponse rapide • Sans engagement</p>

          <div className="mt-10 space-y-4">
            <a href={`tel:${tel}`} data-testid="contact-phone-link" className="flex items-center gap-4 rounded-2xl border border-line bg-white p-4 transition-colors hover:border-pine/40">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-terra text-white shrink-0">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs text-muted2 uppercase tracking-wider font-mono">Téléphone</span>
                <span className="block font-semibold text-ink">{settings.phone}</span>
              </span>
            </a>
            <a href={`mailto:${settings.email}`} data-testid="contact-email-link" className="flex items-center gap-4 rounded-2xl border border-line bg-white p-4 transition-colors hover:border-pine/40">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-terra text-white shrink-0">
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs text-muted2 uppercase tracking-wider font-mono">E-mail</span>
                <span className="block font-semibold text-ink">{settings.email}</span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl bg-white text-ink border border-line p-6 sm:p-9 shadow-[0_24px_60px_-30px_rgba(27,59,43,0.22)]" data-testid="quote-form-container">
            {sent ? (
              <div className="py-14 text-center" data-testid="quote-success">
                <CheckCircle2 className="h-14 w-14 text-pine mx-auto" />
                <h3 className="mt-5 font-display font-semibold text-2xl">Demande envoyée !</h3>
                <p className="mt-3 text-body2 max-w-sm mx-auto">
                  Merci pour votre message. MARY SERVICES vous recontacte rapidement pour étudier votre besoin.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", phone: "", email: "", client_type: "particulier", need_type: NEED_TYPES[0], message: "" }); setFiles([]); }}
                  className="mt-7 text-sm font-semibold text-forest underline underline-offset-4"
                  data-testid="quote-new-request"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <h3 className="font-display font-semibold text-xl sm:text-2xl">Demander mon devis gratuit</h3>

                <div className="grid grid-cols-2 gap-2 rounded-xl bg-sand p-1.5" role="radiogroup" aria-label="Vous êtes">
                  {[
                    { v: "particulier", label: "Particulier" },
                    { v: "professionnel", label: "Professionnel" },
                  ].map((o) => (
                    <button
                      type="button"
                      key={o.v}
                      onClick={() => setForm((f) => ({ ...f, client_type: o.v }))}
                      data-testid={`quote-type-${o.v}`}
                      aria-pressed={form.client_type === o.v}
                      className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${
                        form.client_type === o.v ? "bg-forest text-cream shadow" : "text-body2 hover:text-ink"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.name} onChange={set("name")} placeholder="Nom" aria-label="Nom" data-testid="quote-form-name-input" className={inputCls} />
                  <input required value={form.phone} onChange={set("phone")} placeholder="Téléphone" aria-label="Téléphone" type="tel" data-testid="quote-form-phone-input" className={inputCls} />
                </div>
                <input required value={form.email} onChange={set("email")} placeholder="E-mail" aria-label="E-mail" type="email" data-testid="quote-form-email-input" className={inputCls} />

                <select value={form.need_type} onChange={set("need_type")} aria-label="Type de besoin" data-testid="quote-form-need-select" className={inputCls}>
                  {NEED_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>

                <textarea
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Décrivez votre besoin (surface, accès, délais souhaités…)"
                  aria-label="Message"
                  rows={4}
                  data-testid="quote-form-message-input"
                  className={`${inputCls} resize-none`}
                />

                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    className="hidden"
                    data-testid="quote-form-photos-input"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="quote-photos-button"
                    className="w-full rounded-xl border-2 border-dashed border-line bg-cream px-4 py-5 text-sm text-body2 flex items-center justify-center gap-2.5 transition-colors hover:border-pine hover:text-forest"
                  >
                    <ImagePlus className="h-5 w-5 text-pine" />
                    Joindre des photos du chantier (max 6)
                  </button>
                  {files.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2.5" data-testid="quote-photos-preview">
                      {files.map((f, i) => (
                        <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-sand">
                          <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setFiles(files.filter((_, j) => j !== i))}
                            aria-label="Retirer la photo"
                            className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-ink/70 text-white grid place-items-center"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-form-submit-button"
                  className="w-full relative cta-playful rounded-full bg-terra text-white font-semibold py-4 text-base transition-colors duration-200 hover:bg-[#b05f41] disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {sending && <Loader2 className="h-5 w-5 animate-spin" />}
                  {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                  {!sending && <ArrowRight className="h-4 w-4" />}
                </button>
                <p className="text-xs text-muted2 text-center">
                  Vos données restent privées — voir la politique de confidentialité en bas de page.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
